import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from 'antd';
import { useAuth } from '../../context/AuthContext';
import { AppRoutes } from '../../routes/routeConstants/appRoutes';
import SearchBar from '../../shared/components/SearchBar';
import ClientOption from './ClientOption';
import SelectClientService, { SelectableClient } from '../../services/SelectClient/selectClient.service';
import { SELECT_CLIENT_CONTINUE_LABEL, SELECT_CLIENT_SEARCH_PLACEHOLDER, SELECT_CLIENT_TITLE } from './constants';
import { filterClientsBySearch } from './utils';
import styles from './selectClient.module.scss';

const SelectClient = () => {
  const navigate = useNavigate();
  const { getClients, viewAsClient } = SelectClientService();
  const { data: clients = [] } = useQuery(getClients());
  const { mutate, isPending } = useMutation(viewAsClient());
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SelectableClient | null>(null);
  const { selectedClient } = useAuth();
  const listRef = useRef<HTMLUListElement>(null);

  const filteredClients = useMemo(() => filterClientsBySearch(clients, search), [clients, search]);
  // The client being viewed stays highlighted until another is picked.
  const highlightedId = selected?.id ?? selectedClient?.id;
  const canContinue = !!selected || clients.some(({ id }) => id === selectedClient?.id);
  const isFullListShown = clients.length > 0 && search === '';

  // Brings the highlighted client into view when the full list first shows and whenever a search is cleared.
  useEffect(() => {
    if (isFullListShown) listRef.current?.querySelector('[aria-pressed="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [isFullListShown]);

  // Continuing on the current client needs no new token.
  const handleContinue = () => {
    if (!selected) navigate(AppRoutes.HOME);
    else mutate(selected, { onSuccess: () => navigate(AppRoutes.HOME) });
  };

  return (
    <div className={styles.centeredContainer}>
      <div className={styles.innerContainer}>
        <img className={styles.icon} src="/assets/clientManagerIcon.svg" alt="" aria-hidden="true" />
        <h1 className={styles.title}>{SELECT_CLIENT_TITLE}</h1>
        <div className={styles.card}>
          <SearchBar value={search} onChange={setSearch} placeholder={SELECT_CLIENT_SEARCH_PLACEHOLDER} className={styles.search} />
          <ul ref={listRef} className={styles.list}>
            {filteredClients.map((client) => (
              <li key={client.id}>
                <ClientOption client={client} isSelected={highlightedId === client.id} onSelect={setSelected} />
              </li>
            ))}
          </ul>
        </div>
        <Button className={styles.continueButton} disabled={!canContinue} loading={isPending} onClick={handleContinue}>
          {SELECT_CLIENT_CONTINUE_LABEL}
        </Button>
      </div>
    </div>
  );
};

export default SelectClient;
